// ===================================
// DOCUMENT API SERVICE
// src/services/documentApi.js
// Frontend API calls for document management
// ===================================

import api from './api';

/**
 * Get all user documents
 * @param {Object} filters - Optional filters { sessionId, type }
 */
export const getDocuments = async (filters = {}) => {
  const response = await api.get('/documents', { params: filters });
  return response.data;
};

/**
 * Get single document details
 */
export const getDocument = async (documentId) => {
  const response = await api.get(`/documents/${documentId}`);
  return response.data;
};

/**
 * Save job description text
 * @param {Object} data - { textContent, sessionId, companyName, jobTitle }
 */
export const saveJobDescription = async (data) => {
  const response = await api.post('/documents/job-description', data);
  return response.data;
};

/**
 * Upload job description file
 * @param {File} file - JD file
 * @param {string} sessionId - Optional session ID
 */
export const uploadJobDescription = async (file, sessionId = null) => {
  const formData = new FormData();
  formData.append('jobDescription', file);
  if (sessionId) {
    formData.append('sessionId', sessionId);
  }

  const response = await api.post('/documents/job-description/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Upload generic document
 * @param {File} file - Document file
 * @param {string} documentType - Type: job_description, cover_letter, portfolio, other
 * @param {string} sessionId - Optional session ID
 */
export const uploadDocument = async (file, documentType = 'other', sessionId = null) => {
  const formData = new FormData();
  formData.append('document', file);
  formData.append('documentType', documentType);
  if (sessionId) {
    formData.append('sessionId', sessionId);
  }

  const response = await api.post('/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Delete a document
 */
export const deleteDocument = async (documentId) => {
  const response = await api.delete(`/documents/${documentId}`);
  return response.data;
};

/**
 * Analyze match between resume and job description
 * @param {Object} data - { resumeId, documentId } or { resumeId, jdText }
 */
export const analyzeMatch = async (data) => {
  const response = await api.post('/documents/analyze-match', data);
  return response.data;
};

/**
 * Link document to session
 */
export const linkDocumentToSession = async (documentId, sessionId) => {
  const response = await api.put(`/documents/${documentId}/link-session`, { sessionId });
  return response.data;
};

export default {
  getDocuments,
  getDocument,
  saveJobDescription,
  uploadJobDescription,
  uploadDocument,
  deleteDocument,
  analyzeMatch,
  linkDocumentToSession
};
